import { Auth } from "aws-amplify";
import "../css/auth-theme.css";
import "@aws-amplify/ui-react/styles.css";
import { AmplifyProvider, Authenticator, CheckboxField, useAuthenticator } from "@aws-amplify/ui-react";
import React from "react";
import { useTranslation, Trans } from "react-i18next";

// Custom Sign Up Component with Terms Checkbox
const CustomSignUpFields = () => {
  const { validationErrors } = useAuthenticator();
  const { t } = useTranslation();

  return (
    <>
      <Authenticator.SignUp.FormFields />
      <CheckboxField
        name="terms"
        label={
          <Trans
            i18nKey="SignUp.termsLabel"
            components={[<a href="/terms" className="terms-link" />, <a href="/privacy" className="terms-link" />]}
          />
        }
        errorMessage={validationErrors.terms}
        hasError={!!validationErrors.terms}
        required
      />
    </>
  );
};

const SecurityBadge = () => {
  const { t } = useTranslation();

  return (
    <div className="security-badge">
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path
          d="M12 1L3 5v6c0 5.55 3.84 10.74 9 12 5.16-1.26 9-6.45 9-12V5l-9-4zm0 10.99h7c-.53 4.12-3.28 7.79-7 8.94V12H5V6.3l7-3.11v8.8z"
          fill="#34A853"
        />
        <path d="M12 12.99V19.93c-3.72-1.15-6.47-4.82-7-8.94h7z" fill="#4285F4" />
      </svg>
      <span>{t("SignUp.securityBadge")}</span>
    </div>
  );
};

export default function AuthenticateWrapper({ components }) {
  const { t } = useTranslation();

  const handleGoogleSignIn = () => {
    Auth.federatedSignIn({ provider: "Google" });
  };

  return (
    <AmplifyProvider>
      <div className="auth-container">
        <div className="custom-auth-wrapper">
          <h1 className="app-title">{t("SignUp.title")}</h1>

          <button onClick={handleGoogleSignIn} className="google-signin-button">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="18"
              height="18"
              viewBox="0 0 18 18"
              style={{ marginRight: "12px" }}
            >
              <path
                fill="#4285F4"
                d="M16.8 9.2c0-.6-.1-1.2-.3-1.8H9v3.4h4.3c-.2 1.1-.8 2-1.7 2.6v2.2h2.8c1.6-1.5 2.5-3.7 2.5-6.4z"
              />
              <path
                fill="#34A853"
                d="M9 17c2.3 0 4.2-.7 5.6-2l-2.8-2.2c-.7.5-1.6.8-2.8.8-2.1 0-3.9-1.4-4.5-3.3H1.5v2.3C2.9 14.8 5.7 17 9 17z"
              />
              <path
                fill="#FBBC05"
                d="M4.5 10.3c-.2-.6-.3-1.2-.3-1.8s.1-1.2.3-1.8V4.4H1.5C.8 5.8.4 7.4.4 9s.4 3.2 1.1 4.6l3.4-2.6z"
              />
              <path
                fill="#EA4335"
                d="M9 4.4c1.2 0 2.3.4 3.2 1.3L15 3.1C13.6 1.8 11.5 1 9 1 5.7 1 2.9 3.2 1.5 6l3 2.3C5.1 5.8 6.9 4.4 9 4.4z"
              />
            </svg>
            {t("SignUp.google")}
          </button>

          <div className="security-assurance">
            <SecurityBadge />
          </div>

          <Authenticator
            components={{
              Header: () => null,
              Footer: () => (
                <div className="custom-footer">
                  <a href="/privacy" className="privacy-link">
                    {t("SignUp.privacySecurity")}
                  </a>
                </div>
              ),
              SignUp: { FormFields: CustomSignUpFields },
            }}
            services={{
              async validateCustomSignUp(formData) {
                const { t } = await import("react-i18next");
                const errors = {};

                if (!formData.email) {
                  errors.email = t("SignUp.errors.emailRequired");
                }

                if (!formData.terms) {
                  errors.terms = t("SignUp.errors.termsRequired");
                }

                return Object.keys(errors).length > 0 ? errors : undefined;
              },
            }}
            signUpAttributes={["email"]}
            loginMechanisms={["email"]}
          >
            {components}
            {({ signOut, user }) => <div style={{ display: "none" }}></div>}
          </Authenticator>
        </div>
      </div>
    </AmplifyProvider>
  );
}
